/* global BigInt */
/**
 * Position Data Service Layer
 * 
 * Browser-first implementation using Supabase client directly.
 * Designed for easy migration to backend/API calls later.
 * To replace with backend: swap function implementations to call API endpoints
 * instead of Supabase client, keeping the same function signatures.
 */

import { supabase } from '../supabase/supabaseClient';
import {
  calculateWeightedAvgEntry,
  calculateCostBasisAfterSell,
  calculateRealizedPnL,
} from '../helpers/positionMath';

/**
 * Upsert (create or update) user game position
 * @param {object} positionData - position data
 * @returns {Promise<object>} upserted position or error
 */
export async function upsertUserGamePosition(positionData) {
  const {
    walletAddress,
    gameId,
    gamePda,
    mint,
    tokenBalance,
    totalTokensBought,
    totalTokensSold,
    totalTokensRedeemed,
    totalSolSpentLamports,
    totalSolReceivedLamports,
    costBasisLamports,
    avgEntryLamportsPerToken,
    realizedPnlLamports,
    positionStatus,
    openedAt,
    closedAt,
    lastTxSignature,
  } = positionData;

  // Validate required fields
  if (!walletAddress || !gameId || !gamePda || !mint) {
    throw new Error('Missing required position fields');
  }

  const { data, error } = await supabase
    .from('user_game_positions')
    .upsert(
      {
        wallet_address: walletAddress,
        game_id: Number(gameId),
        game_pda: gamePda,
        mint,
        token_balance: Number(tokenBalance),
        total_tokens_bought: Number(totalTokensBought || 0),
        total_tokens_sold: Number(totalTokensSold || 0),
        total_tokens_redeemed: Number(totalTokensRedeemed || 0),
        total_sol_spent_lamports: Number(totalSolSpentLamports || 0),
        total_sol_received_lamports: Number(totalSolReceivedLamports || 0),
        cost_basis_lamports: Number(costBasisLamports || 0),
        avg_entry_lamports_per_token: Number(avgEntryLamportsPerToken || 0),
        realized_pnl_lamports: Number(realizedPnlLamports || 0),
        position_status: positionStatus || 'open',
        opened_at: openedAt,
        closed_at: closedAt,
        last_tx_signature: lastTxSignature,
        updated_at: new Date().toISOString(),
      },
      {
        onConflict: 'wallet_address,game_id',
      }
    )
    .select()
    .single();

  if (error) {
    console.error('Failed to upsert position:', error);
    throw new Error(`Failed to update position: ${error.message}`);
  }

  return data;
}

/**
 * Fetch user's position for a specific game
 * @param {string} walletAddress - user wallet address
 * @param {string} gameId - game ID
 * @returns {Promise<object|null>} position data or null if not found
 */
export async function fetchUserGamePosition(walletAddress, gameId) {
  if (!walletAddress || !gameId) {
    return null;
  }

  const { data, error } = await supabase
    .from('user_game_positions')
    .select('*')
    .eq('wallet_address', walletAddress)
    .eq('game_id', gameId)
    .maybeSingle();

  if (error) {
    console.error('Failed to fetch position:', error);
    throw new Error(`Failed to fetch position: ${error.message}`);
  }

  return data;
}

/**
 * Record a successful buy transaction
 * Creates trade event and updates position
 * @param {object} buyData - buy transaction data
 * @returns {Promise<object>} updated position
 */
export async function recordSuccessfulBuy(buyData) {
  const {
    walletAddress,
    gameId,
    gamePda,
    mint,
    txSignature,
    tokenAmountAtomic, // tokens received in atoms
    solSpentLamports, // net SOL spent in lamports
  } = buyData;

  // Step 1: Fetch existing position (if any)
  const existingPosition = await fetchUserGamePosition(walletAddress, gameId);

  // Step 2: Calculate new position values
  const existingTokenBalance = window.BigInt(existingPosition?.token_balance || 0);
  const existingTotalBought = window.BigInt(existingPosition?.total_tokens_bought || 0);
  const existingTotalSolSpent = window.BigInt(existingPosition?.total_sol_spent_lamports || 0);
  const existingCostBasis = window.BigInt(existingPosition?.cost_basis_lamports || 0);

  const tokenAmountBigInt = window.BigInt(tokenAmountAtomic);
  const solSpentBigInt = window.BigInt(solSpentLamports);

  const newTokenBalance = existingTokenBalance + tokenAmountBigInt;
  const newTotalBought = existingTotalBought + tokenAmountBigInt;
  const newTotalSolSpent = existingTotalSolSpent + solSpentBigInt;
  const newCostBasis = existingCostBasis + solSpentBigInt;

  // Calculate weighted average entry
  const newAvgEntry = calculateWeightedAvgEntry(
    existingCostBasis.toString(),
    existingTokenBalance.toString(),
    solSpentBigInt.toString(),
    tokenAmountBigInt.toString()
  );

  // Step 3: Upsert position
  const updatedPosition = await upsertUserGamePosition({
    walletAddress,
    gameId,
    gamePda,
    mint,
    tokenBalance: newTokenBalance.toString(),
    totalTokensBought: newTotalBought.toString(),
    totalTokensSold: existingPosition?.total_tokens_sold || 0,
    totalTokensRedeemed: existingPosition?.total_tokens_redeemed || 0,
    totalSolSpentLamports: newTotalSolSpent.toString(),
    totalSolReceivedLamports: existingPosition?.total_sol_received_lamports || 0,
    costBasisLamports: newCostBasis.toString(),
    avgEntryLamportsPerToken: newAvgEntry,
    realizedPnlLamports: existingPosition?.realized_pnl_lamports || 0,
    positionStatus: 'open',
    openedAt: existingPosition?.opened_at || new Date().toISOString(),
    closedAt: null,
    lastTxSignature: txSignature,
  });

  return updatedPosition;
}

/**
 * Record a successful sell transaction
 * Creates trade event and updates position
 * @param {object} sellData - sell transaction data
 * @returns {Promise<object>} updated position
 */
export async function recordSuccessfulSell(sellData) {
  const {
    walletAddress,
    gameId,
    gamePda,
    mint,
    txSignature,
    tokenAmountAtomic, // tokens sold in atoms
    solReceivedLamports, // net SOL received in lamports
  } = sellData;

  // Step 1: Fetch existing position (required for sell)
  const existingPosition = await fetchUserGamePosition(walletAddress, gameId);

  if (!existingPosition) {
    throw new Error('Cannot record sell - no existing position found');
  }

  // Step 2: Calculate new position values
  const existingTokenBalance = window.BigInt(existingPosition.token_balance || 0);
  const existingTotalSold = window.BigInt(existingPosition.total_tokens_sold || 0);
  const existingTotalSolReceived = window.BigInt(existingPosition.total_sol_received_lamports || 0);
  const existingCostBasis = window.BigInt(existingPosition.cost_basis_lamports || 0);
  const existingAvgEntry = existingPosition.avg_entry_lamports_per_token || 0;
  const existingRealizedPnL = window.BigInt(existingPosition.realized_pnl_lamports || 0);

  const tokenAmountBigInt = window.BigInt(tokenAmountAtomic);
  const solReceivedBigInt = window.BigInt(solReceivedLamports);

  const newTokenBalance = existingTokenBalance - tokenAmountBigInt;
  const newTotalSold = existingTotalSold + tokenAmountBigInt;
  const newTotalSolReceived = existingTotalSolReceived + solReceivedBigInt;

  // Calculate cost basis reduction
  const newCostBasis = calculateCostBasisAfterSell(
    existingCostBasis.toString(),
    existingAvgEntry.toString(),
    tokenAmountBigInt.toString()
  );

  // Calculate realized P&L
  const newRealizedPnL = calculateRealizedPnL(
    existingRealizedPnL.toString(),
    solReceivedBigInt.toString(),
    existingAvgEntry.toString(),
    tokenAmountBigInt.toString()
  );

  // Determine position status
  const newPositionStatus = newTokenBalance === 0n ? 'closed' : 'open';
  const closedAt = newPositionStatus === 'closed' ? new Date().toISOString() : null;

  // Step 3: Upsert position
  const updatedPosition = await upsertUserGamePosition({
    walletAddress,
    gameId,
    gamePda,
    mint,
    tokenBalance: newTokenBalance.toString(),
    totalTokensBought: existingPosition.total_tokens_bought || 0,
    totalTokensSold: newTotalSold.toString(),
    totalTokensRedeemed: existingPosition.total_tokens_redeemed || 0,
    totalSolSpentLamports: existingPosition.total_sol_spent_lamports || 0,
    totalSolReceivedLamports: newTotalSolReceived.toString(),
    costBasisLamports: newCostBasis,
    avgEntryLamportsPerToken: existingAvgEntry, // avg entry doesn't change on sell
    realizedPnlLamports: newRealizedPnL,
    positionStatus: newPositionStatus,
    openedAt: existingPosition.opened_at,
    closedAt,
    lastTxSignature: txSignature,
  });

  return updatedPosition;
}

/**
 * Refresh position for the active game
 * Re-fetches the position from the database
 * @param {string} walletAddress - user wallet address
 * @param {string} gameId - game ID
 * @returns {Promise<object|null>} refreshed position data
 */
export async function refreshPositionForActiveGame(walletAddress, gameId) {
  return fetchUserGamePosition(walletAddress, gameId);
}
